(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.ProductCostCalculator = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var RUNTIME_CONTRACT_VERSION = '2.0.0';
  var MINIMUM_CONFIG_VERSION = 17;

  function number(value, label) {
    var parsed = Number(value);

    if (!Number.isFinite(parsed)) {
      throw new Error(label + ': valore numerico non valido (' + String(value) + ')');
    }

    return parsed;
  }

  function ceilTo(value, step) {
    var numericStep = number(step, 'Scatto di arrotondamento');

    if (numericStep <= 0) {
      throw new Error('Lo scatto di arrotondamento deve essere maggiore di zero.');
    }

    return Math.ceil((value - Number.EPSILON) / numericStep) * numericStep;
  }

  function validateConfig(config) {
    if (!config || config.schemaVersion !== RUNTIME_CONTRACT_VERSION) {
      throw new Error('Contratto economico Preventivometro 2.0.0 non riconosciuto.');
    }

    if (
      Number(config.configVersion) < MINIMUM_CONFIG_VERSION ||
      config.publishable !== true ||
      config.validationStatus !== 'OK'
    ) {
      throw new Error('La configurazione Preventivometro deve essere V17 o successiva, validata e pubblicabile.');
    }

    if (
      !config.common ||
      !config.common.materials ||
      !config.common.machines ||
      !config.models ||
      !config.models.targa_summa
    ) {
      throw new Error('Configurazione Preventivometro incompleta.');
    }

    return true;
  }

  function externalCutSurcharge(areaMm2, thicknessMm, plexiglasConfig) {
    var rule = plexiglasConfig.externalCutSurcharge;

    if (thicknessMm !== rule.thicknessMm) {
      return 0;
    }

    var areaSpan = rule.largeReferenceAreaMm2 - rule.smallReferenceAreaMm2;
    var amountSpan = rule.largeAmount - rule.smallAmount;

    return rule.smallAmount + (
      (areaMm2 - rule.smallReferenceAreaMm2) / areaSpan
    ) * amountSpan;
  }

  function supportRate(materialId, thicknessMm, supports) {
    var support = supports[materialId];

    if (!support) {
      throw new Error('Materiale non configurato: ' + materialId);
    }

    var baseRate = support.costPerSqm;
    if (materialId === 'plexiglas') {
      baseRate = support.ratesByThickness[String(thicknessMm)];
      if (!Number.isFinite(baseRate)) {
        throw new Error('Spessore Plexiglas non configurato: ' + thicknessMm + ' mm');
      }
    }

    return {
      baseRate: baseRate,
      wasteRate: support.wasteRate,
      rateWithWaste: baseRate * (1 + support.wasteRate)
    };
  }

  function calculateTargaSummaV17(input, config) {
    validateConfig(config);

    if (input.modelId && input.modelId !== 'targa_summa') {
      throw new Error('Modello non supportato: ' + input.modelId);
    }

    var widthMm = number(input.widthMm, 'Larghezza');
    var heightMm = number(input.heightMm, 'Altezza');
    var materialId = String(input.materialId || '').trim();
    var thicknessMm = materialId === 'plexiglas'
      ? number(input.thicknessMm, 'Spessore')
      : (input.thicknessMm === undefined || input.thicknessMm === null
        ? null
        : number(input.thicknessMm, 'Spessore'));
    var backgroundColor = String(input.backgroundColor || 'bianco').trim().toLowerCase();
    var drilling = String(input.drilling || 'Nessuna').trim();
    var edgePolishing = Boolean(input.edgePolishing);
    var bevels = Boolean(input.bevels);
    var artworkMinutes = number(input.artworkMinutes === undefined ? 0 : input.artworkMinutes, 'Composizione grafica');

    if (widthMm <= 0 || heightMm <= 0) {
      return {
        status: 'DIMENSIONI_NON_VALIDE',
        inputs: Object.assign({}, input, {
          widthMm: widthMm,
          heightMm: heightMm,
          thicknessMm: thicknessMm,
          materialId: materialId
        })
      };
    }

    var common = config.common;
    var model = config.models.targa_summa;
    var machine = common.machines[model.machineId];
    var minSideMm = Math.min(widthMm, heightMm);
    var maxSideMm = Math.max(widthMm, heightMm);

    if (!machine) {
      throw new Error('Macchina Summa non configurata: ' + model.machineId);
    }

    if (minSideMm > machine.bandWidthMm) {
      return {
        status: 'NON_STAMPABILE',
        inputs: Object.assign({}, input, {
          widthMm: widthMm,
          heightMm: heightMm,
          thicknessMm: thicknessMm,
          materialId: materialId
        }),
        geometry: {
          bandWidthMm: machine.bandWidthMm,
          reason: 'Entrambi i lati superano la larghezza utile della Summa.'
        }
      };
    }

    var sideOnMachineWidthMm = maxSideMm <= machine.bandWidthMm ? maxSideMm : minSideMm;
    var printAdvanceMm = maxSideMm <= machine.bandWidthMm ? minSideMm : maxSideMm;
    var areaMm2 = widthMm * heightMm;
    var areaM2 = areaMm2 / 1000000;
    var perimeterM = 2 * (widthMm + heightMm) / 1000;
    var realBands = Math.ceil(printAdvanceMm / machine.bandAdvanceMm);
    var ribbonBands = realBands + machine.startupRibbonBandsPerCycle;
    var usesWhiteVinyl = model.whiteVinylSupportIds.indexOf(materialId) !== -1;
    var printVinylKind = usesWhiteVinyl ? 'white' : 'transparent';
    var printVinylId = model.printVinylIds[printVinylKind];
    var whitePasses = usesWhiteVinyl
      ? machine.whitePassesOnWhiteVinyl
      : machine.whitePassesOnTransparentVinyl;
    var passesPerBand = machine.cmykPasses + whitePasses;
    var printMinutes = realBands * passesPerBand * machine.secondsPerPass / 60;

    var ribbons = common.materials.ribbons;
    var ribbonCostPerBand = (
      (machine.cmykPasses - 1) * ribbons.ribbon_summa_cmy.costPerBandPass +
      ribbons.ribbon_summa_k.costPerBandPass +
      whitePasses * ribbons.ribbon_summa_w.costPerBandPass
    );
    var ribbonCost = ribbonBands * ribbonCostPerBand;
    var printVinyl = common.materials.vinyls[printVinylId];
    var printedVinylLengthMm = realBands * machine.bandAdvanceMm;
    var consumedVinylLengthMm = printedVinylLengthMm + machine.unprintedVinylWasteMmPerCycle;
    var consumedVinylAreaM2 = consumedVinylLengthMm / 1000 * machine.bandWidthMm / 1000;
    var printVinylCost = consumedVinylAreaM2 * printVinyl.costPerSqm;
    var printHeadCost = ribbonBands * common.materials.printHead.costPerRibbonBand;
    var separateRenewalCost = ribbonBands * machine.separateRenewalCostPerRibbonBand;
    var rawPrintConsumables = ribbonCost + printVinylCost + printHeadCost + separateRenewalCost;
    var machineCost = printMinutes / 60 * machine.hourlyCost;
    var printBeforeRetry = rawPrintConsumables * (1 + common.commercial.printMarkup) + machineCost;
    var printWithMarkupAndRetry = printBeforeRetry * (1 + machine.printRetryRate);
    var orderHandlingCost = common.commercial.orderHandlingCost;
    var printSaleTotal = printWithMarkupAndRetry + orderHandlingCost;

    var rate = supportRate(materialId, thicknessMm, common.materials.supports);
    var supportRaw = areaM2 * rate.rateWithWaste;
    var supportBaseRounded = ceilTo(supportRaw, common.rounding.supportBase.step);
    var isPlexiglas = materialId === 'plexiglas';
    var backgroundVinylId = isPlexiglas ? model.backgroundVinylIds[backgroundColor] : null;

    if (isPlexiglas && !backgroundVinylId) {
      throw new Error('Colore fondo non configurato: ' + backgroundColor);
    }

    var backgroundMaterialCost = isPlexiglas
      ? areaM2 * common.materials.vinyls[backgroundVinylId].costPerSqm
      : 0;
    var cutSurcharge = isPlexiglas
      ? externalCutSurcharge(areaMm2, thicknessMm, common.materials.supports.plexiglas)
      : 0;
    var supportSaleBeforeCut = ceilTo(
      (supportBaseRounded + backgroundMaterialCost) * (1 + common.commercial.supportMarkup),
      common.rounding.supportSale.step
    );
    var supportSaleTotal = supportSaleBeforeCut + cutSurcharge;

    var labor = common.labor;
    if (!Object.prototype.hasOwnProperty.call(labor.drillingMinutes, drilling)) {
      throw new Error('Foratura non configurata: ' + drilling);
    }

    var backgroundApplicationMinutes = isPlexiglas ? labor.plexiglasBackgroundApplicationMinutes : 0;
    var cleaningMinutes = areaM2 / labor.cleaningReferenceAreaM2 * labor.cleaningMinutesPerReferenceArea;
    var drillingMinutes = labor.drillingMinutes[drilling];
    var bevelMinutes = isPlexiglas && bevels ? perimeterM * labor.bevelMinutesPerPerimeterM : 0;
    var laborMinutes = artworkMinutes +
      labor.printedVinylApplicationMinutes +
      backgroundApplicationMinutes +
      labor.scrapSelectionMinutes +
      labor.supportCuttingMinutes +
      labor.deburringMinutes +
      cleaningMinutes +
      labor.finalInspectionMinutes +
      drillingMinutes +
      bevelMinutes;
    var edgePolishingAllowed = isPlexiglas && thicknessMm !== labor.edgePolishingExcludedThicknessMm;
    var edgePolishingApplied = edgePolishing && edgePolishingAllowed;
    var edgePolishingCost = edgePolishingApplied ? perimeterM * labor.edgePolishingCostPerPerimeterM : 0;
    var timedLaborCost = laborMinutes / 60 * labor.hourlyCost;
    var laborTotal = timedLaborCost + edgePolishingCost;

    var preVat = printSaleTotal + supportSaleTotal + laborTotal;
    var vat = preVat * common.tax.vatRate;
    var totalVatIncluded = preVat + vat;
    var roundedSalePrice = ceilTo(totalVatIncluded, common.rounding.finalSale.step);
    var industrialCost = (
      (rawPrintConsumables + machineCost) * (1 + machine.printRetryRate) +
      orderHandlingCost +
      supportRaw +
      cutSurcharge +
      backgroundMaterialCost +
      laborTotal
    );

    return {
      status: 'OK',
      configVersion: config.configVersion,
      validationStatus: config.validationStatus,
      inputs: {
        widthMm: widthMm,
        heightMm: heightMm,
        thicknessMm: thicknessMm,
        materialId: materialId,
        backgroundColor: backgroundColor,
        drilling: drilling,
        edgePolishing: edgePolishing,
        bevels: bevels,
        artworkMinutes: artworkMinutes
      },
      geometry: {
        areaMm2: areaMm2,
        areaM2: areaM2,
        perimeterM: perimeterM,
        sideOnMachineWidthMm: sideOnMachineWidthMm,
        printAdvanceMm: printAdvanceMm,
        realBands: realBands,
        ribbonBands: ribbonBands
      },
      print: {
        feedModel: machine.vinylFeedModel,
        printVinylKind: printVinylKind,
        printVinylId: printVinylId,
        whitePasses: whitePasses,
        passesPerBand: passesPerBand,
        minutes: printMinutes,
        printedVinylLengthMm: printedVinylLengthMm,
        consumedVinylLengthMm: consumedVinylLengthMm,
        consumedVinylAreaM2: consumedVinylAreaM2,
        ribbonCost: ribbonCost,
        printVinylCost: printVinylCost,
        printHeadCost: printHeadCost,
        separateRenewalCost: separateRenewalCost,
        rawConsumables: rawPrintConsumables,
        machineCost: machineCost,
        printBeforeRetry: printBeforeRetry,
        retryRate: machine.printRetryRate,
        withMarkupAndRetry: printWithMarkupAndRetry,
        orderHandlingCost: orderHandlingCost,
        total: printSaleTotal
      },
      support: {
        materialId: materialId,
        baseRate: rate.baseRate,
        wasteRate: rate.wasteRate,
        rateWithWaste: rate.rateWithWaste,
        raw: supportRaw,
        baseRounded: supportBaseRounded,
        backgroundVinylId: backgroundVinylId,
        backgroundMaterialCost: backgroundMaterialCost,
        externalCutSurcharge: cutSurcharge,
        saleBeforeExternalCut: supportSaleBeforeCut,
        total: supportSaleTotal
      },
      labor: {
        minutes: laborMinutes,
        cleaningMinutes: cleaningMinutes,
        backgroundApplicationMinutes: backgroundApplicationMinutes,
        drillingMinutes: drillingMinutes,
        bevelMinutes: bevelMinutes,
        timedCost: timedLaborCost,
        edgePolishingAllowed: edgePolishingAllowed,
        edgePolishingApplied: edgePolishingApplied,
        edgePolishingCost: edgePolishingCost,
        total: laborTotal
      },
      totals: {
        preVat: preVat,
        vat: vat,
        totalVatIncluded: totalVatIncluded,
        roundedSalePrice: roundedSalePrice,
        roundingAdjustment: roundedSalePrice - totalVatIncluded,
        industrialCost: industrialCost,
        grossMargin: preVat - industrialCost
      }
    };
  }

  function calculateProductCost(input, config, modelId) {
    var selectedModelId = modelId || input.modelId || 'targa_summa';

    if (selectedModelId !== 'targa_summa') {
      return {
        status: 'MODELLO_NON_SUPPORTATO',
        modelId: selectedModelId
      };
    }

    return calculateTargaSummaV17(Object.assign({}, input, { modelId: selectedModelId }), config);
  }

  return {
    contractVersion: RUNTIME_CONTRACT_VERSION,
    calculateProductCost: calculateProductCost,
    calculateTargaSummaV17: calculateTargaSummaV17,
    validateConfig: validateConfig,
    _internal: {
      ceilTo: ceilTo,
      externalCutSurcharge: externalCutSurcharge,
      supportRate: supportRate
    }
  };
});
